import { useEffect, useId, useSyncExternalStore } from 'react'
import { useBlocker } from 'react-router-dom'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'

/**
 * "Leave page?" protection for unsaved edits (E-UX).
 *
 * Any form calls `useUnsavedChanges(isDirty)`. One `<UnsavedChangesGuard />` in the app shell
 * then (a) holds in-app navigation (links, back button) and asks first, and (b) makes the browser
 * show its own "Leave site?" prompt on reload / tab close — while ANY registered form is dirty.
 * A single guard is required: React Router supports one navigation blocker at a time, and a page
 * can hold several independent forms.
 */
const dirtyForms = new Set<string>()
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())
const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l) } }
const snapshot = () => dirtyForms.size

export function useUnsavedChanges(dirty: boolean): void {
  const id = useId()
  useEffect(() => {
    if (dirty) dirtyForms.add(id); else dirtyForms.delete(id)
    emit()
    return () => { if (dirtyForms.delete(id)) emit() }
  }, [dirty, id])
}

export function UnsavedChangesGuard() {
  const count = useSyncExternalStore(subscribe, snapshot, () => 0)
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    dirtyForms.size > 0 && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search))

  useEffect(() => {
    if (count === 0) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [count])

  // Saving (which clears dirty) while a navigation is held lets it through.
  useEffect(() => { if (count === 0 && blocker.state === 'blocked') blocker.proceed() }, [count, blocker])

  return (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      title="Leave without saving?"
      message="Your changes on this page have not been saved. If you leave now they will be lost."
      confirmLabel="Leave page"
      cancelLabel="Stay on page"
      destructive
      onCancel={() => blocker.reset?.()}
      onConfirm={() => { dirtyForms.clear(); emit(); blocker.proceed?.() }}
    />
  )
}
